import { useEffect, useState } from "react";
import { Clock } from "lucide-react";

/**
 * Compact hours ribbon shown at the very top of the site. Highlights today's
 * status live (abierto ahora / cierra en X min / cerrado los miércoles) so it
 * cannot be missed. Style: hairline gold strip with tracking, subtle red flash
 * on Wednesdays.
 */
const HOURS = [
  { open: [12, 30], close: [16, 30] },
  { open: [20, 0], close: [23, 30] },
];

const getStatus = () => {
  const now = new Date();
  const day = now.getDay(); // 0 Sun … 6 Sat
  const mins = now.getHours() * 60 + now.getMinutes();
  const toMin = ([h, m]) => h * 60 + m;

  if (day === 3) return { state: "closed_wed" };
  for (const s of HOURS) {
    const o = toMin(s.open), c = toMin(s.close);
    if (mins >= o && mins < c) {
      const leftMin = c - mins;
      return { state: "open", leftMin };
    }
  }
  const next = HOURS.find((s) => mins < toMin(s.open));
  if (next) return { state: "closed_soon", nextOpen: next.open };
  return { state: "closed" };
};

const fmt = ([h, m]) => `${h}:${String(m).padStart(2, "0")}`;

export const HoursRibbon = ({ lang = "es" }) => {
  const [status, setStatus] = useState(getStatus());
  useEffect(() => {
    const id = setInterval(() => setStatus(getStatus()), 60_000);
    return () => clearInterval(id);
  }, []);

  const isEs = lang === "es";
  const isWed = status.state === "closed_wed";

  const statusLabel = (() => {
    if (isWed) return isEs ? "Hoy cerrado" : "Closed today";
    if (status.state === "open") {
      const h = Math.floor(status.leftMin / 60);
      const m = status.leftMin % 60;
      const rest = h ? `${h} h ${m} min` : `${m} min`;
      return isEs ? `Abierto · cierra en ${rest}` : `Open · closes in ${rest}`;
    }
    if (status.state === "closed_soon" && status.nextOpen) {
      return isEs
        ? `Cerrado · abrimos a las ${fmt(status.nextOpen)}`
        : `Closed · opens at ${fmt(status.nextOpen)}`;
    }
    return isEs ? "Cerrado ahora" : "Closed now";
  })();

  return (
    <div
      data-testid="hours-ribbon"
      className={`w-full border-b transition-colors ${
        isWed
          ? "border-[#A91D11]/50 bg-gradient-to-r from-[#1a0806] via-[#0F0908] to-[#1a0806]"
          : "border-gold/25 bg-[#0A0A0A]/80"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-10 py-1 md:py-1.5 flex items-center justify-between gap-2 md:gap-3 text-[9px] md:text-[11px] uppercase tracking-[0.15em] md:tracking-[0.2em]">
        {/* Left: live status */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <span
            data-testid="hours-dot"
            className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
              status.state === "open"
                ? "bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.7)] animate-pulse"
                : "bg-[#A91D11] shadow-[0_0_10px_rgba(169,29,17,0.6)]"
            }`}
          />
          <span
            data-testid="hours-status"
            className={`${
              status.state === "open" ? "text-emerald-300" : "text-[#F0B486]"
            } whitespace-nowrap`}
          >
            {statusLabel}
          </span>
        </div>

        {/* Middle: schedule (hidden on very small screens) */}
        <div className="hidden sm:flex items-center gap-2 text-neutral-400 whitespace-nowrap flex-shrink min-w-0 truncate">
          <Clock size={11} className="text-gold flex-shrink-0" />
          <span>
            {isEs ? "Jue-Mar" : "Thu-Tue"} · 12:30–16:30 · 20:00–23:30
          </span>
        </div>

        {/* Right: emphasized closed-on-Wed */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="hidden md:inline text-neutral-500">·</span>
          <span
            data-testid="hours-wed-closed"
            className="text-[#F0B486] font-medium"
          >
            <span className="hidden sm:inline">{isEs ? "Miércoles" : "Wednesday"}</span>
            <span className="sm:hidden">{isEs ? "Mié" : "Wed"}</span>
            <span className="text-neutral-500 mx-1.5">·</span>
            <span className="text-[#A91D11]">{isEs ? "CERRADO" : "CLOSED"}</span>
          </span>
        </div>
      </div>
    </div>
  );
};
